import { ReactElement, ReactNode, useState } from 'react'
import { useForm } from '@tanstack/react-form'
import { z } from 'zod'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material'
import { ConfirmIcon, FeedbackIcon } from 'src/v3/core/icons'
import Loading from 'src/v3/components/loading'

// This file is the one copy of the survey: its thirteen questions, their exact
// wording, the 1–5 scale, and the markup for each field. Three surfaces render
// it — the Event certificate flow, the v1 classroom course-feedback page, and
// the classroom-v2 wizard — and a learner's answers have to mean the same thing
// in all three, because they aggregate into the same accreditation reporting.
// Editing a label here changes what the data means everywhere, which is the
// point: there is nowhere else to edit it.
//
// It is split into two layers, following the same split as
// components/quiz/quiz-taker.tsx:
//
//   - The exported question tables and field components are PRESENTATIONAL.
//     They hold no state, submit nothing, and decide nothing about validity;
//     the caller passes values down and receives changes back.
//   - The default export is a self-contained form built on top of them, with
//     its own tanstack-form state, all-thirteen-required validation, and submit
//     handling. That is what the two v1 surfaces use and its props have not
//     changed.
//
// classroom-v2 uses the presentational layer directly, because its answers live
// in the wizard slice and its submit is a dispatch rather than an awaited
// callback. All three surfaces require all thirteen questions.

let validTextSchema = z
  .string()
  .min(1, 'This field is required')
  .regex(/.*\S.*/, 'This field is required')

let likertSchema = z.number({ message: 'This field is required' })

export type SurveyFormValues = {
  likertContentRelevantToPractice: number | null
  likertLearningObjectivesMet: number | null
  likertContentWellOrganized: number | null
  likertVisualsSupportedLearning: number | null
  likertVisualDesignEnhancedLearning: number | null
  likertPresentationErrorFree: number | null
  likertPresenterEngaging: number | null
  likertPresenterKnowledgeable: number | null
  likertNoBiasOrCommercialism: number | null
  likertWillImpactPractice: number | null
  freeTextMostEffectiveAspects: string | null
  freeTextAreasForImprovement: string | null
  freeTextFutureTopicRequests: string | null
}

// Field names split by answer type, derived from SurveyFormValues rather than
// listed again, so a rename can't leave a table pointing at a field that no
// longer exists.
export type SurveyLikertKey = {
  [K in keyof SurveyFormValues]: SurveyFormValues[K] extends number | null
    ? K
    : never
}[keyof SurveyFormValues]

export type SurveyFreeTextKey = {
  [K in keyof SurveyFormValues]: SurveyFormValues[K] extends string | null
    ? K
    : never
}[keyof SurveyFormValues]

// Question order IS display order, and the displayed number is the position in
// this array — so the labels carry no hard-coded "1." that could drift out of
// step with where the question actually sits.
export let surveyLikertQuestions: { name: SurveyLikertKey; label: string }[] = [
  {
    name: 'likertContentRelevantToPractice',
    label: 'The content presented was relevant to your practice.',
  },
  {
    name: 'likertLearningObjectivesMet',
    label:
      'Overall, the educational activity addressed each established learning objective.',
  },
  {
    name: 'likertContentWellOrganized',
    label: 'The content was well-organized and moved logically.',
  },
  {
    name: 'likertVisualsSupportedLearning',
    label:
      'The content was supported appropriately by examples, graphs, tables, photos, and documented references that added to my comprehension and understanding.',
  },
  {
    name: 'likertVisualDesignEnhancedLearning',
    label:
      'The visual design of the educational activity enhanced your learning experience.',
  },
  {
    name: 'likertPresentationErrorFree',
    label:
      'The presentation materials were free of typos, spelling, and grammar errors.',
  },
  {
    name: 'likertPresenterEngaging',
    label: 'The presenter spoke clearly and in an engaging manner.',
  },
  {
    name: 'likertPresenterKnowledgeable',
    label:
      'The presenter demonstrated a strong understanding of the learning objectives, even if they had to go without relying on visual aids.',
  },
  {
    name: 'likertNoBiasOrCommercialism',
    label:
      'The activity did not promote a product or exhibit a commercial bias.',
  },
  {
    name: 'likertWillImpactPractice',
    label:
      'The knowledge I gained from this educational activity will impact my practice in a positive way.',
  },
]

export let surveyFreeTextQuestions: {
  name: SurveyFreeTextKey
  label: string
}[] = [
  {
    name: 'freeTextMostEffectiveAspects',
    label:
      'What aspects of the course were most effective or valuable? What should we continue doing in future sessions?',
  },
  {
    name: 'freeTextAreasForImprovement',
    label:
      'What areas of the course could be improved? Please share any suggestions for enhancing future sessions.',
  },
  {
    name: 'freeTextFutureTopicRequests',
    label:
      'Are there any specific topics or areas of interest you would like us to cover in future educational offerings or courses?',
  },
]

// Which of the thirteen are still unanswered, in display order. Every question is
// required on every surface — a survey is only accepted as evidence of
// participation if it was actually filled in — so this is the one place that rule
// is expressed, and the v1 form's per-field validators and the v2 wizard's
// submit gate both mean the same thing by "answered".
//
// The v2 API accepts nulls (its own validation is only 1–5-when-present), so the
// requirement is enforced here rather than relied on from the server.
// Whitespace-only free text counts as unanswered, matching the v1 regex.
export let unansweredSurveyFields = (
  values: SurveyFormValues,
): (SurveyLikertKey | SurveyFreeTextKey)[] => [
  ...surveyLikertQuestions
    .filter((question) => values[question.name] === null)
    .map((question) => question.name),
  ...surveyFreeTextQuestions
    .filter((question) => (values[question.name] ?? '').trim() === '')
    .map((question) => question.name),
]

// The numeric value each option stores and the phrase it means. Both are shown:
// the radio's visible label is the number (compact, and what the legend
// explains) while its accessible name carries the phrase, so a screen reader
// user isn't asked to memorize the mapping.
export let surveyScale: { value: number; label: string }[] = [
  { value: 1, label: 'Strongly Disagree' },
  { value: 2, label: 'Disagree' },
  { value: 3, label: 'Neutral' },
  { value: 4, label: 'Agree' },
  { value: 5, label: 'Strongly Agree' },
]

// Off-screen but still in the accessibility tree. display:none or
// visibility:hidden would remove it, which is the opposite of the intent.
let visuallyHidden = {
  position: 'absolute',
  left: '-10000px',
  width: '1px',
  height: '1px',
} as const

export let SurveyIntro = (): ReactElement => (
  <Box sx={{ mb: 4 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, m: 2 }}>
      <Avatar
        sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}
        role="img"
        aria-label="Feedback icon"
      >
        <FeedbackIcon />
      </Avatar>
      <Box>
        <Typography
          variant="h5"
          sx={{ fontWeight: 700 }}
          component="h1"
          id="survey-title"
        >
          Post-Course Survey and Evaluation
        </Typography>
        <Typography
          variant="subtitle1"
          color="textSecondary"
          id="survey-description"
        >
          We value your feedback and use it to improve future learning
          experiences for all of our learners. Thank you for taking the time to
          share your thoughts!
        </Typography>
      </Box>
    </Box>
  </Box>
)

let SurveySection = ({
  headingId,
  children,
}: {
  headingId: string
  children: ReactNode
}): ReactElement => (
  <Card
    elevation={0}
    sx={{
      p: { xs: 2.5, sm: 4 },
      bgcolor: 'background.paperDark',
      borderRadius: 3,
      mb: 4,
      border: '1px solid',
      borderColor: 'divider',
    }}
    component="section"
    aria-labelledby={headingId}
  >
    {children}
  </Card>
)

// Section wrapper for the ten rated statements, including the scale legend that
// the per-question descriptions repeat for assistive tech.
export let SurveyLikertSection = ({
  children,
}: {
  children: ReactNode
}): ReactElement => (
  <SurveySection headingId="likert-section-heading">
    <Typography
      color="text.primary"
      variant="body1"
      component="h2"
      id="likert-section-heading"
      sx={{ mb: 2, fontSize: '1.1rem', fontWeight: 600 }}
    >
      Likert Scale Questions: Please rate the following statements using the
      scale below.
    </Typography>
    <Typography
      color="text.secondary"
      variant="body2"
      sx={{ mb: 4, p: 2, bgcolor: 'background.paper', borderRadius: 2 }}
    >
      <strong>Scale:</strong>
      {surveyScale.map((option) => (
        <Box key={option.value} component="span" sx={{ display: 'block' }}>
          {option.value} – {option.label}
        </Box>
      ))}
    </Typography>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {children}
    </Box>
  </SurveySection>
)

export let SurveyFreeTextSection = ({
  children,
}: {
  children: ReactNode
}): ReactElement => (
  <SurveySection headingId="text-section-heading">
    <Typography
      variant="h6"
      component="h2"
      id="text-section-heading"
      sx={{
        mb: 1,
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <ConfirmIcon color="primary" aria-hidden="true" />
      Short Answer Questions
    </Typography>
    <Typography color="text.secondary" variant="body2" sx={{ mb: 3 }}>
      Please take a quick moment to share your honest feedback about this
      course.
    </Typography>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {children}
    </Box>
  </SurveySection>
)

// One rated statement. Controlled and stateless: `value` is whatever the caller
// holds and `onChange` is the only way it moves. `isRequired` is the caller's
// call rather than a property of the question, so this stays reusable if a future
// surface ever asks fewer; it drives the visible asterisk and aria-required
// together, so the two can't disagree. All three surfaces today pass true.
export let SurveyLikertQuestion = ({
  name,
  label,
  number,
  value,
  error,
  isRequired,
  isDisabled,
  onChange,
  onBlur,
}: {
  name: SurveyLikertKey
  label: string
  number: number
  value: number | null
  error?: string
  isRequired: boolean
  isDisabled?: boolean
  onChange: (value: number) => void
  onBlur?: () => void
}): ReactElement => {
  let legendId = `${name}-legend`
  let descriptionId = `${name}-description`
  let errorId = `${name}-error`
  let hasError = error !== undefined

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        bgcolor: 'background.paperDark',
        borderRadius: 2,
        border: '1px solid',
        borderColor: hasError ? 'error.main' : 'transparent',
      }}
      role="group"
      aria-labelledby={legendId}
      aria-describedby={hasError ? errorId : descriptionId}
    >
      <FormControl
        error={hasError}
        component="fieldset"
        sx={{ width: '100%' }}
        required={isRequired}
      >
        <FormLabel
          component="legend"
          id={legendId}
          sx={{ mb: 2, color: hasError ? 'error.main' : 'text.primary' }}
        >
          {number}. {label}
        </FormLabel>
        {/* The scale is stated once in the section legend for sighted users;
            this repeats it per-question for assistive tech, which has no
            equivalent of glancing back up at it. */}
        <Box id={descriptionId} sx={visuallyHidden}>
          Rate from 1 (Strongly Disagree) to 5 (Strongly Agree)
          {isRequired ? '' : '. Optional.'}
        </Box>
        {hasError && (
          <Typography
            color="error.main"
            id={errorId}
            role="alert"
            aria-live="polite"
            sx={{ fontSize: '0.875rem', mb: 1 }}
          >
            {error}
          </Typography>
        )}
        <RadioGroup
          aria-labelledby={legendId}
          aria-required={isRequired}
          aria-invalid={hasError}
          name={name}
          value={value?.toString() ?? ''}
          onChange={(event) => onChange(Number(event.target.value))}
          onBlur={onBlur}
          sx={{
            flexDirection: 'row',
            gap: 2,
            '& .MuiFormControlLabel-root': {
              bgcolor: 'background.paper',
              borderRadius: 1,
              px: 2,
              py: 1,
              m: 0,
              transition: 'all 0.2s ease-in-out',
              '&:hover': { bgcolor: 'primary.dark' },
              '&:focus-within': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: '2px',
              },
            },
            '& .Mui-checked + .MuiFormControlLabel-label': {
              color: 'primary.main',
              fontWeight: 600,
            },
          }}
        >
          {surveyScale.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value.toString()}
              disabled={isDisabled}
              control={
                <Radio
                  size="small"
                  slotProps={{
                    input: {
                      'aria-label': `${option.value} - ${option.label}`,
                    },
                  }}
                />
              }
              label={option.value.toString()}
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.875rem',
                  fontWeight: 500,
                },
              }}
            />
          ))}
        </RadioGroup>
      </FormControl>
    </Paper>
  )
}

export let SurveyFreeTextQuestion = ({
  name,
  label,
  number,
  value,
  error,
  isRequired,
  isDisabled,
  onChange,
  onBlur,
}: {
  name: SurveyFreeTextKey
  label: string
  number: number
  value: string | null
  error?: string
  isRequired: boolean
  isDisabled?: boolean
  onChange: (value: string) => void
  onBlur?: () => void
}): ReactElement => {
  let errorId = `${name}-error`
  let hasError = error !== undefined

  return (
    <TextField
      name={name}
      value={value ?? ''}
      onChange={(event) => onChange(event.target.value)}
      onBlur={onBlur}
      disabled={isDisabled}
      error={hasError}
      helperText={error}
      label={`${number}. ${label}`}
      required={isRequired}
      multiline
      minRows={3}
      variant="outlined"
      fullWidth
      sx={{
        '& .MuiOutlinedInput-root': {
          bgcolor: 'background.paper',
          borderRadius: 2,
          '& fieldset': { borderColor: 'divider' },
          '&:hover fieldset': { borderColor: 'primary.main' },
          '&.Mui-focused fieldset': { borderColor: 'primary.main' },
        },
      }}
      slotProps={{
        htmlInput: {
          'aria-required': isRequired,
          'aria-invalid': hasError,
          'aria-describedby': hasError ? errorId : undefined,
        },
        formHelperText: {
          id: errorId,
          role: hasError ? 'alert' : undefined,
          'aria-live': hasError ? 'polite' : undefined,
        },
      }}
    />
  )
}

// The self-contained form: owns its answers, requires every question, and runs
// the caller's submit to completion itself. Used by the Event certificate flow
// and the v1 classroom feedback page, both of which POST directly from a
// callback and have no store to keep answers in.
//
// classroom-v2 deliberately does NOT use this. Its answers belong in the wizard
// slice alongside the rest of the compliance state, and awaiting a dispatch to
// drive what happens next is the pattern REDUX_USAGE.md rules out — so it
// composes the presentational exports above instead.
export default ({
  onSubmit,
  initialValues,
}: {
  onSubmit: (values: SurveyFormValues) => Promise<void>
  initialValues?: Partial<SurveyFormValues>
}): ReactElement => {
  let [loading, setLoading] = useState<boolean>(false)
  let [errorMessage, setErrorMessage] = useState<string>('')
  let [submitError, setSubmitError] = useState<string>('')

  let form = useForm({
    defaultValues: {
      likertContentRelevantToPractice:
        initialValues?.likertContentRelevantToPractice ?? null,
      likertLearningObjectivesMet:
        initialValues?.likertLearningObjectivesMet ?? null,
      likertContentWellOrganized:
        initialValues?.likertContentWellOrganized ?? null,
      likertVisualsSupportedLearning:
        initialValues?.likertVisualsSupportedLearning ?? null,
      likertVisualDesignEnhancedLearning:
        initialValues?.likertVisualDesignEnhancedLearning ?? null,
      likertPresentationErrorFree:
        initialValues?.likertPresentationErrorFree ?? null,
      likertPresenterEngaging: initialValues?.likertPresenterEngaging ?? null,
      likertPresenterKnowledgeable:
        initialValues?.likertPresenterKnowledgeable ?? null,
      likertNoBiasOrCommercialism:
        initialValues?.likertNoBiasOrCommercialism ?? null,
      likertWillImpactPractice: initialValues?.likertWillImpactPractice ?? null,
      freeTextMostEffectiveAspects:
        initialValues?.freeTextMostEffectiveAspects ?? null,
      freeTextAreasForImprovement:
        initialValues?.freeTextAreasForImprovement ?? null,
      freeTextFutureTopicRequests:
        initialValues?.freeTextFutureTopicRequests ?? null,
    },
    onSubmit: async ({ value }) => {
      setLoading(true)
      setSubmitError('')
      try {
        await onSubmit(value as SurveyFormValues)
      } catch (err: any) {
        let error = err?.response?.data || 'Survey submission failed'
        setErrorMessage(error)
        setSubmitError(error)
      } finally {
        setLoading(false)
      }
    },
  })

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        form.handleSubmit()
      }}
      aria-label="Post-Course Survey and Evaluation"
      noValidate
    >
      {submitError && (
        <Box
          role="alert"
          aria-live="assertive"
          sx={{
            mb: 3,
            p: 2,
            bgcolor: 'error.dark',
            color: 'error.contrastText',
            borderRadius: 2,
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            Error: {submitError}
          </Typography>
        </Box>
      )}
      <SurveyIntro />
      <SurveyLikertSection>
        {surveyLikertQuestions.map((question, index) => (
          <form.Field
            key={question.name}
            name={question.name}
            validators={{
              onChange: ({ value }) => {
                let result = likertSchema.safeParse(value)
                return result.success
                  ? undefined
                  : result.error.issues[0]?.message
              },
            }}
          >
            {(field) => (
              <SurveyLikertQuestion
                name={question.name}
                label={question.label}
                number={index + 1}
                value={field.state.value}
                error={field.state.meta.errors[0] ?? undefined}
                isRequired
                onChange={(value) => field.handleChange(value)}
                onBlur={field.handleBlur}
              />
            )}
          </form.Field>
        ))}
      </SurveyLikertSection>
      <SurveyFreeTextSection>
        {surveyFreeTextQuestions.map((question, index) => (
          <form.Field
            key={question.name}
            name={question.name}
            validators={{
              onChange: ({ value }) => {
                let result = validTextSchema.safeParse(value)
                return result.success
                  ? undefined
                  : result.error.issues[0]?.message
              },
            }}
          >
            {(field) => (
              <SurveyFreeTextQuestion
                name={question.name}
                label={question.label}
                number={index + 1}
                value={field.state.value}
                error={field.state.meta.errors[0] ?? undefined}
                isRequired
                onChange={(value) => field.handleChange(value)}
                onBlur={field.handleBlur}
              />
            )}
          </form.Field>
        ))}
      </SurveyFreeTextSection>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 4 }}>
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={loading}
          aria-label={
            loading ? 'Submitting survey, please wait' : 'Submit survey'
          }
        >
          {loading ? 'Submitting...' : 'Submit Survey'}
        </Button>
      </Box>
      <Box
        role="status"
        aria-live="polite"
        aria-atomic="true"
        sx={visuallyHidden}
      >
        {loading ? 'Survey is being submitted' : ''}
      </Box>
      <Loading entities={[]} loading={loading} />
      <Snackbar
        open={!!errorMessage}
        onClose={() => setErrorMessage('')}
        aria-live="assertive"
        aria-atomic="true"
      >
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
          role="alert"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </form>
  )
}
